/**
 * THE SCOPE MODEL'S STORED SHAPES — a job type's rate parts, and the lines that
 * quantify them.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * A PART POINTS AT A SHARED VARIABLE, OR IT CARRIES A LITERAL. NEVER BOTH,
 * NEVER NEITHER — AND THE ALTERNATIVE IS THE WHOLE FIELD.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * `parts` is what makes one Assumptions edit re-price every line that reads it.
 * A part whose source is a VARIABLE resolves through the organisation's shared
 * assumptions at evaluation time; a part with a FIXED value is a literal this
 * type owns. Flatten the two into a plain number and the screen still renders,
 * the arithmetic still produces figures, and the thing has stopped being a
 * model — editing an assumption changes nothing and nobody is told.
 *
 * So the alternative is a DISCRIMINATED union on `kind` rather than two optional
 * fields. Two optionals admit `{}` and `{ value, variable }`, and both are
 * states nothing downstream can act on: the first has no rate and the second has
 * two. A tagged union makes each unrepresentable rather than merely refused —
 * the same argument `coverage-formula.schema.ts` makes about cycles.
 *
 * ⚠️ THE TAG IS NOT A DESIGN CHOICE I MADE. It is what the Scope UI already
 * binds to. The database comment on `RoleJobType.parts` describes the shape as
 * `{ key, label, unit, default? | variableRef? }`; the string `variableRef`
 * appears NOWHERE in the UI that consumes it, which reads `source.kind`. The
 * comment describes an earlier design, and a contract written from it would
 * typecheck perfectly against a client that could never send it.
 */
import { z } from "zod";

/**
 * A part's key: a stable identifier a formula references, never a display
 * string.
 *
 * Same constraint as `CoverageInputKeySchema`, and deliberately the same
 * constraint rather than a looser one — a job type's parts and a coverage
 * formula's inputs are both "things an expression names", and two different
 * rules for one concept is how a key that works in one place is refused in the
 * other.
 */
export const RoleJobTypePartKeySchema = z.string().regex(/^[a-z][a-zA-Z0-9_]{0,63}$/, {
  message: "part key must start with a lowercase letter, then letters, digits or underscores"
});

/** The two arms, named once so the refusal below can enumerate them. */
const ROLE_JOB_TYPE_PART_SOURCE_ARMS = [
  z.strictObject({
    kind: z.literal("variable"),
    /** The shared assumption's key. Resolved at evaluation time, never inlined. */
    variable: RoleJobTypePartKeySchema
  }),
  z.strictObject({
    kind: z.literal("fixed"),
    /**
     * `z.number()` ALREADY REFUSES `Infinity`, `-Infinity` AND `NaN` in zod 4 —
     * measured, not assumed, by deleting a `.refine(Number.isFinite)` here and
     * watching every test stay green. The refine that used to sit on this line
     * was dead weight buying only a custom message, so it is gone rather than
     * left to read as the thing doing the work.
     */
    value: z.number()
  })
] as const;

/**
 * Every accepted `kind`, READ OFF THE ARMS rather than restated.
 *
 * A third arm added above appears in the refusal with no second edit. A hand-kept
 * list is the failure this whole file argues against in another form — a message
 * that describes a contract it is no longer part of.
 */
const ROLE_JOB_TYPE_PART_SOURCE_KINDS = ROLE_JOB_TYPE_PART_SOURCE_ARMS.flatMap(
  (arm) => arm.shape.kind.def.values
);

/**
 * What the caller actually sent, said back to them.
 *
 * A MISSING `kind` and a WRONG one must not read the same — that is half the
 * defect: zod's own answer is `Invalid input` for both, so `{}` and
 * `{"kind":"constant"}` were indistinguishable and a caller could not tell
 * "you forgot the tag" from "that is not one of the tags".
 */
function describeKind(source: unknown): string {
  if (typeof source !== "object" || source === null || Array.isArray(source)) {
    return `${describeValue(source)}, which is not an object`;
  }
  const { kind } = source as { kind?: unknown };
  return kind === undefined ? "nothing" : describeValue(kind);
}

/**
 * One value, said back, WITHOUT a call that can throw.
 *
 * This runs inside a zod error map, so anything thrown here replaces a 400 that
 * names the field with a 500 that names nothing — and both `JSON.stringify` (a
 * cyclic value, a BigInt) and a template literal (a symbol) throw on inputs a
 * hand-built `.parse()` argument can carry. Quoting is reserved for the string
 * case, which is the only one where a caller's value and its rendering could
 * otherwise be confused.
 */
function describeValue(value: unknown): string {
  switch (typeof value) {
    case "string":
      return JSON.stringify(value);
    case "object":
      return value === null ? "null" : Array.isArray(value) ? "a list" : "an object";
    case "function":
      return "a function";
    default:
      // number, boolean, bigint, symbol, undefined — `String` is total on all
      // five, where `${}` throws on a symbol.
      return String(value);
  }
}

/**
 * Where a part's number comes from.
 *
 * `variable` names a shared assumption by key and is the reason this field
 * exists at all. `fixed` is a literal this job type owns.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * 🚨 THE REFUSAL ENUMERATES THE ARMS, BECAUSE NOTHING ELSE ON ANY SURFACE
 * DOES. THAT IS NEX-3631.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * zod's own message for a discriminator it cannot match is the bare string
 * `Invalid input`, at path `…source.kind`, and it is BYTE-IDENTICAL for a missing
 * `kind` and a wrong one — so `{}` and `{"kind":"constant"}` are the same answer.
 * Every sibling field in the same response enumerates: `basis` answers
 * *"Invalid option: expected one of "SALARY"|"HOURLY"|…"*, `group` the same. This
 * one field broke the pattern, and it is the one a caller cannot guess: no
 * metadata endpoint lists the kinds, the database comment describes a
 * `variableRef` shape that was never real, and the reporter resolved it by
 * guessing one value at a time.
 *
 * So the message says which words work, distinguishes ABSENT from WRONG, and
 * names the field each arm then requires — because learning `kind: "variable"` is
 * accepted and then discovering `variable` separately is the same round trip
 * twice.
 */
export const RoleJobTypePartSourceSchema = z.discriminatedUnion(
  "kind",
  ROLE_JOB_TYPE_PART_SOURCE_ARMS,
  {
    error: (issue) => {
      const accepted = ROLE_JOB_TYPE_PART_SOURCE_KINDS.map((value) => JSON.stringify(value)).join(
        "|"
      );
      return (
        `Invalid option: expected one of ${accepted}, received ${describeKind(issue.input)} — ` +
        `"variable" then requires variable: <part key>, "fixed" requires value: <number>`
      );
    }
  }
);

/** One rate input of a job type. */
export const RoleJobTypePartSchema = z.strictObject({
  /** The name the formulas reference this term by. */
  key: RoleJobTypePartKeySchema,
  /** What the term is called in the type's drawer. */
  label: z.string().trim().min(1).max(160),
  /**
   * How the term reads — "€ a year", "%", "hours".
   *
   * DISPLAY ONLY, and deliberately a free string rather than an enum. The
   * arithmetic comes from `basis` and the formulas; this is what a human sees
   * beside the number. An enum here would refuse a legitimate unit until
   * somebody shipped a deploy, which is the argument `schema.prisma` already
   * makes for the currency column.
   */
  unit: z.string().max(80),
  source: RoleJobTypePartSourceSchema
});

/**
 * Every part of one job type.
 *
 * 🚨 AT LEAST ONE PART, AND THIS IS A WRITE-BOUNDARY REFUSAL RATHER THAN A
 * DEGRADATION. A job type's expressions are evaluated against a scope built
 * ENTIRELY from its parts — `derive.ts` writes `scope[part.key]` and nothing
 * else — so a type with no parts has an empty scope: every expression naming a
 * term fails, and a constant-only one prices at zero. Both outcomes are a scope
 * line silently worth nothing, which is the failure this whole model exists to
 * make impossible.
 *
 * Measured before choosing the refusal: **0 of the 13 job types in the Scope
 * UI's reference fixture declare `parts: []`**, and the UI has no flow that
 * creates a job type at all today (`JobTypesTab` carries no handlers). So this
 * refuses nothing a client can currently send. `derive.test.ts` does pass `[]`
 * — that is a unit test exercising a degenerate branch, not a product state.
 *
 * 🚨 KEYS ARE UNIQUE WITHIN A TYPE, because a formula references a part BY KEY.
 * Two parts named `wage` make `wage` ambiguous, and an evaluator resolving it
 * would pick one silently — a rate that is wrong by whatever the other one said.
 * `CoverageFormulaSchema` refuses a duplicate input key for the identical
 * reason, and this is the same rule at the other end of the same reference.
 */
export const RoleJobTypePartsSchema = z
  .array(RoleJobTypePartSchema)
  .min(1, {
    message:
      "a job type needs at least one part — a type with no rate inputs prices every scope line that uses it at zero"
  })
  .max(32)
  .superRefine((parts, ctx) => {
    const seen = new Set<string>();
    parts.forEach((part, index) => {
      if (seen.has(part.key)) {
        ctx.addIssue({
          code: "custom",
          path: [index, "key"],
          message: `duplicate part key "${part.key}" — a formula referencing it would be ambiguous`
        });
      }
      seen.add(part.key);
    });
  });

/** Where a part's number comes from. */
export type RoleJobTypePartSource = z.infer<typeof RoleJobTypePartSourceSchema>;

/** One rate input of a job type. */
export type RoleJobTypePart = z.infer<typeof RoleJobTypePartSchema>;

/** Every part of one job type. */
export type RoleJobTypeParts = z.infer<typeof RoleJobTypePartsSchema>;
